import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const daysLeft = (target) => {
  if (!target) return null;
  return Math.ceil((new Date(target).getTime() - Date.now()) / 86400000);
};

export default function ProductionNcrRegister() {
  const { user } = useAuth();
  const [ncrs, setNcrs] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const [resolution, setResolution] = useState("");

  const canClose = ["admin", "planner", "quality", "quality_inspector", "production_manager"].includes(user?.role);

  const load = async () => {
    setNcrs(null);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/production-ncrs", { params });
      setNcrs(data || []);
    } catch (err) {
      setNcrs([]);
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const submitClose = async () => {
    setBusy(true);
    try {
      await api.post(`/production-ncrs/${closeTarget.id}/close`, { resolution });
      toast.success(`${closeTarget.ncr_no} closed`);
      setCloseTarget(null);
      setResolution("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Close failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="production-ncr-register">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Production NCRs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Non-Conformances auto-raised from Testing-stage QC entries that were out of spec. Closure is due within 7 days of raising.
          </p>
        </div>
        <Link to="/quality-inspections"><Button variant="outline">Go to Quality Inspections</Button></Link>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {["all", "OPEN", "CLOSED"].map((k) => (
          <button
            key={k}
            onClick={() => setStatusFilter(k)}
            data-testid={`pncr-filter-${k}`}
            className={`text-[11px] px-3 py-1 rounded-full border transition ${statusFilter === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}
          >
            {k === "all" ? "All" : k.charAt(0) + k.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Production Non-Conformance Reports</div>
        {ncrs === null && <div className="py-10 text-center text-sm text-slate-500">Loading…</div>}
        {ncrs && ncrs.length === 0 && (
          <div className="py-14 text-center text-slate-400">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">No Production NCRs — nothing has come back out of spec.</p>
          </div>
        )}
        {ncrs && ncrs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-4">NCR No</th><th className="py-2 px-4">Job</th><th className="py-2 px-4">Item</th>
                  <th className="py-2 px-4">Failed Qty</th><th className="py-2 px-4">Raised</th><th className="py-2 px-4">Closure Due</th>
                  <th className="py-2 px-4">Status</th><th className="py-2 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {ncrs.map((n) => {
                  const dleft = daysLeft(n.target_date);
                  const overdue = n.status === "OPEN" && dleft !== null && dleft < 0;
                  return (
                    <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-4 font-mono font-semibold text-blue-700">{n.ncr_no}</td>
                      <td className="py-2 px-4 mono">{n.job_code || "—"}</td>
                      <td className="py-2 px-4">
                        <div className="mono text-slate-500">{n.item_code || "—"}</div>
                        <div className="text-slate-700">{n.item_name || "—"}</div>
                      </td>
                      <td className="py-2 px-4 mono text-red-600">{n.failed_qty}</td>
                      <td className="py-2 px-4">{n.created_at ? new Date(n.created_at).toLocaleDateString() : "—"}</td>
                      <td className={`py-2 px-4 ${overdue ? "text-red-600 font-semibold" : ""}`}>
                        {n.target_date ? new Date(n.target_date).toLocaleDateString() : "—"}
                        {overdue && <span className="ml-1"><AlertTriangle className="inline w-3 h-3" /> Overdue</span>}
                      </td>
                      <td className="py-2 px-4">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${n.status === "CLOSED" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                          {n.status}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        {n.status === "OPEN" && canClose && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setCloseTarget(n); setResolution(""); }} data-testid={`pncr-close-${n.ncr_no}`}>
                            Close
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={!!closeTarget} onOpenChange={(v) => !v && setCloseTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Close {closeTarget?.ncr_no}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Resolution</Label>
            <Textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Corrective action taken…" data-testid="pncr-resolution-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)}>Cancel</Button>
            <Button onClick={submitClose} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="pncr-close-confirm">Close NCR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
